import { configuration as config } from './utils'
import httpAuth from './httpAuth'
import dayjs from 'dayjs'
import {
  ApiApplicationDetails,
  ApiApplicationStatusResponse,
  ApiResponse,
  EmptyEmploymentInformation,
  ErrorResponse,
  StatusObject,
} from '@/types'
import { AxiosError } from 'axios'
// import redirection from '@services/redirection'

const LSP_BASE = `${config.BASE_API_URL}/lsp`
const LSP_APPLICATION_BASE = `${LSP_BASE}/applications`
const LSP_OPTS_BASE = `${LSP_BASE}/options`
const LSP_ORDERS_BASE = `${LSP_BASE}/orders`
const LSP_CHECKOUT_BASE = `${LSP_BASE}/checkout`
const ERR_MESSAGE = (error) => error.response?.data?.message ?? error.message ?? error.errorMessage

const LSP = {
  // get top 3 recent payments
  async getRecentPayments() {
    try {
      const res = await httpAuth.get(`${LSP_BASE}/recent-payments`).then((r) => r.data)
      return { status: true, message: res.message, data: res.data }
    } catch (error) {
      return {
        status: false,
        message: ERR_MESSAGE(error),
      }
    }
  },
  // get top 3 recent applications
  async getRecentApplications() {
    try {
      const res = await httpAuth.get(`${LSP_BASE}/recent-applications`).then((r) => r.data)
      return { status: true, message: res.message, data: res.data }
    } catch (error) {
      return {
        status: false,
        message: ERR_MESSAGE(error),
      }
    }
  },
  // get application history
  async getApplicationHistory(appId: string) {
    try {
      const res = await httpAuth.get(`${LSP_APPLICATION_BASE}/${appId}/application-history`)
      return { status: true, message: res.data.successMessage, data: res.data }
    } catch (error) {
      return {
        status: false,
        message: ERR_MESSAGE(error),
        data: null,
      }
    }
  },
  // get application information for application
  async getPrimaryApplicantInformation(appId: string) {
    try {
      const res = await httpAuth.get(`${LSP_APPLICATION_BASE}/${appId}/applicant-information`)
      return { status: true, message: res.data.successMessage, data: res.data }
    } catch (error) {
      return {
        status: false,
        message: ERR_MESSAGE(error),
        data: null,
      }
    }
  },
  // all for current user.
  async all(active = false) {
    try {
      const res = await httpAuth.get(`${LSP_APPLICATION_BASE}?active=${active}`)
      return { status: true, message: res.data.successMessage, data: res.data.applications }
    } catch (error) {
      return {
        status: false,
        message: ERR_MESSAGE(error),
        data: null,
      }
    }
  },
  async one(appId: string): Promise<StatusObject<ApiApplicationDetails> | StatusObject<null>> {
    try {
      const res = await httpAuth.get(`${LSP_APPLICATION_BASE}/${appId}`)
      return { status: true, message: 'successful', data: res.data.application }
    } catch (error) {
      console.log(error)
      return {
        status: false,
        message: ERR_MESSAGE(error),
      }
    }
  },
  async getPDF(appId) {
    try {
      return await httpAuth.get(`${LSP_APPLICATION_BASE}/${appId}/pdf`).then((response) => response.data)
    } catch (error) {
      console.log(error)
      return {
        status: false,
        message: ERR_MESSAGE(error),
      }
    }
  },
  async approvalPdf(appId) {
    try {
      return await httpAuth.get(`${LSP_APPLICATION_BASE}/${appId}/approval-pdf`).then((response) => response.data)
    } catch (error) {
      console.log(error)
      return {
        status: false,
        message: ERR_MESSAGE(error),
      }
    }
  },
  async create(application) {
    try {
      const newApplication = {
        ...application,
        dateOfBirth: dayjs(application.dateOfBirth).format('YYYY-MM-DD'),
        residenceIssueDate: dayjs(application.residenceIssueDate).format('YYYY-MM-DD'),
      }
      const res = await httpAuth.post(LSP_APPLICATION_BASE, newApplication)
      return { status: true, message: res.data.message, data: res.data.data.applicationCode }
    } catch (error) {
      console.log(error)
      return {
        status: false,
        message: ERR_MESSAGE(error),
      }
    }
  },
  async update(appId, applicant) {
    try {
      return await httpAuth.put(LSP_APPLICATION_BASE + '/' + appId, applicant).then((response) => response.data)
    } catch (error) {
      console.log(error)
      return {
        status: false,
        message: ERR_MESSAGE(error),
      }
    }
  },
  async saveEmploymentInfo(appId, employment) {
    try {
      const response = await httpAuth
        .post(`${LSP_APPLICATION_BASE}/${appId}/employment`, employment)
        .then((response) => response.data)
      return { status: true, message: 'Successfully Saved', data: response.data as EmptyEmploymentInformation }
    } catch (error) {
      console.log(error)
      return {
        status: false,
        message: ERR_MESSAGE(error),
      }
    }
  },
  async saveTravelInfo(appId, travelInfo) {
    try {
      const result = await httpAuth
        .post(`${LSP_APPLICATION_BASE}/${appId}/travel`, travelInfo)
        .then((response) => response.data)
      return { status: true, message: result.message, data: result.data }
    } catch (error) {
      console.log(error)
      return {
        status: false,
        message: ERR_MESSAGE(error),
      }
    }
  },
  async uploadDocument(appId, data) {
    try {
      const fData = new FormData()

      // for multipart axios requires FormData object
      for (const key in data) {
        fData.append(key, data[key])
      }
      fData.append('dateOfIssue', dayjs(data.dateIssued).format('YYYY-MM-DD'))
      fData.set('dateOfExpiry', dayjs(data.dateExpired).format('YYYY-MM-DD'))
      return await httpAuth.post(LSP_APPLICATION_BASE + `/${appId}/document`, fData).then((response) => response.data)
    } catch (error) {
      console.log(error)
      return {
        status: false,
        message: ERR_MESSAGE(error),
      }
    }
  },
  async getDocument(appId) {
    try {
      const result = await httpAuth.get(`${LSP_APPLICATION_BASE}/${appId}/document`).then((response) => response.data)
      return { status: true, message: result.message, data: result.data }
    } catch (error) {
      console.log(error)
      return {
        status: false,
        message: ERR_MESSAGE(error),
      }
    }
  },
  async uploadSupportDocument(appId, data) {
    try {
      const fData = new FormData()
      fData.append('type', data.type)
      fData.append('applicantSupportDocument', data.file)
      return await httpAuth
        .post(`${LSP_APPLICATION_BASE}/${appId}/supporting-documents`, fData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then((response) => response.data)
    } catch (error) {
      console.log(error)
      return {
        status: false,
        message: ERR_MESSAGE(error),
      }
    }
  },
  async getSupportDocument(appId, type) {
    try {
      return await httpAuth
        .get(`${LSP_APPLICATION_BASE}/${appId}/supporting-documents/${type}`)
        .then((response) => response.data)
    } catch (error) {
      console.log(error)
      return {
        status: false,
        message: ERR_MESSAGE(error),
      }
    }
  },
  async removeSupportDocument(appId, type) {
    try {
      return await httpAuth
        .delete(`${LSP_APPLICATION_BASE}/${appId}/supporting-documents/${type}`)
        .then((response) => response.data)
    } catch (error) {
      console.log(error)
      return {
        status: false,
        message: ERR_MESSAGE(error),
      }
    }
  },
  async submit(appId) {
    try {
      return await httpAuth
        .put(`${LSP_APPLICATION_BASE}/${appId}/submit`) // no data needs to be provided.
        .then((response) => response.data)
    } catch (error) {
      console.log(error)
      return {
        status: false,
        message: ERR_MESSAGE(error),
        data: error.response?.data?.data ?? null,
      }
    }
  },
  async cancelApplication(appId) {
    try {
      const result = await httpAuth
        .put(`${LSP_APPLICATION_BASE}/${appId}/cancel`) // no data needs to be provided.
        .then((response) => response.data)
      return { status: true, message: result.message, data: null }
    } catch (error) {
      console.log(error)
      return {
        status: false,
        message: ERR_MESSAGE(error),
        data: error.response?.data?.data ?? null,
      }
    }
  },
  options: {
    async applicationStatuses(): Promise<StatusObject<ApiApplicationStatusResponse[]> | StatusObject<null>> {
      try {
        const result: ApiResponse<ApiApplicationStatusResponse[]> = await httpAuth.get(
          `${LSP_OPTS_BASE}/application-status`,
        )
        return {
          status: true,
          data: result.data,
        }
      } catch (error: unknown) {
        const errorMessage =
          error instanceof AxiosError ? (error.response as unknown as ErrorResponse)?.message : (error as Error).message
        return {
          status: false,
          message: errorMessage,
        }
      }
    },
    async statusList() {
      try {
        const result = await httpAuth.get(`${LSP_OPTS_BASE}/application-status`)
        return { status: true, message: 'Success', data: result.data }
      } catch (error) {
        return { status: false, message: 'Failed to fetch status list' }
      }
    },
  },
  dependents: {
    async add(appId, dependent) {
      try {
        return await httpAuth
          .post(LSP_APPLICATION_BASE + `/${appId}/dependents`, dependent)
          .then((response) => response.data)
      } catch (error) {
        console.log(error)
        return {
          status: false,
          message: ERR_MESSAGE(error),
        }
      }
    },
    async update(appId, data) {
      try {
        data.isAccompanying = !!data.isAccompanying
        return await httpAuth
          .put(LSP_APPLICATION_BASE + `/${appId}/dependents/${data.applicantCode}`, data)
          .then((response) => response.data)
      } catch (error) {
        console.log(error)
        return {
          status: false,
          message: ERR_MESSAGE(error),
        }
      }
    },
    async remove(appId, applicantId) {
      try {
        const result = await httpAuth
          .delete(LSP_APPLICATION_BASE + `/${appId}/dependents/${applicantId}`)
          .then((response) => response.data)
        return { status: true, message: result.message }
      } catch (error) {
        console.log(error)
        return {
          status: false,
          message: ERR_MESSAGE(error),
        }
      }
    },
    async uploadDocument(appId, dependentId, data) {
      try {
        const fData = new FormData()

        // for multipart axios requires FormData object
        for (const key in data) fData.append(key, data[key])

        fData.append('dateOfIssue', dayjs(data.dateIssued).format('YYYY-MM-DD'))
        fData.set('dateOfExpiry', dayjs(data.dateExpired).format('YYYY-MM-DD'))

        return await httpAuth
          .post(LSP_APPLICATION_BASE + `/${appId}/dependents/${dependentId}/document`, fData)
          .then((response) => response.data)
      } catch (error) {
        console.log(error)
        return {
          status: false,
          message: ERR_MESSAGE(error),
        }
      }
    },
    async getDocument(appId, dependentId) {
      try {
        const result = await httpAuth
          .get(`${LSP_APPLICATION_BASE}/${appId}/dependents/${dependentId}/document`)
          .then((response) => response.data)
        return { status: true, message: result.message, data: result.data }
      } catch (error) {
        console.log(error)
        return {
          status: false,
          message: ERR_MESSAGE(error),
        }
      }
    },
  },

  applicant: {
    selfEmployed(application) {
      // possible values ['IDK','YES','NO']
      const IDK = -1
      const NO = 0
      const YES = 1
      const MISTAKE = 2

      const docTypes = application.applicant.supportDocuments.filter(
        (item) => item.documentType == 'JOB' || item.documentType == 'REB',
      )

      if (docTypes.length == 0) return IDK
      if (docTypes.length == 1 && docTypes[0].documentType == 'JOB') return NO
      if (docTypes.length == 1 && docTypes[0].documentType == 'REB') return YES
      if (docTypes.length > 1)
        // Why?
        return MISTAKE
    },
  },
  payment: {
    async getApplicantPaymentSummary(applicationId: string, applicantId: string) {
      try {
        const result = await httpAuth.get(`${LSP_APPLICATION_BASE}/${applicationId}/payment-summary/${applicantId}`)
        return result.data
      } catch (error) {
        return {
          status: false,
          message: ERR_MESSAGE(error),
        }
      }
    },
    async getPaymentBreakdown(applicationId: string) {
      try {
        const result = await httpAuth.get(`${LSP_APPLICATION_BASE}/${applicationId}/payment-breakdown`)
        return result.data
      } catch (error) {
        return {
          status: false,
          message: ERR_MESSAGE(error),
        }
      }
    },
    async getLSPRedirection(applicationId: string, applicantId: string, buyerInformation: any) {
      try {
        const result = await httpAuth.post(`${LSP_CHECKOUT_BASE}`, {
          service: 'LSP',
          applicationId: applicationId,
          applicantId: applicantId,
          billingInformation: {
            ...buyerInformation,
          },
        })

        return result.data
      } catch (error) {
        const res = {
          status: false,
          message: ERR_MESSAGE(error),
        }
        if (error?.response?.data.validationErrors) {
          res['validationErrors'] = error?.response?.data.validationErrors
        }
        return res
      }
    },
    async getOrders(includeAuthorizationCus) {
      try {
        const result = await httpAuth.get(`${LSP_ORDERS_BASE}?includeAuthorizationCus=${includeAuthorizationCus}`)
        return result.data
      } catch (error) {
        return {
          status: false,
          message: ERR_MESSAGE(error),
        }
      }
    },
    async getOrderDetails(orderId: number) {
      try {
        const result = await httpAuth.get(`${LSP_ORDERS_BASE}/${orderId}`)
        return result.data
      } catch (error) {
        return {
          status: false,
          message: ERR_MESSAGE(error),
        }
      }
    },
    async getReceiptPaymentPDF(appId: string, orderId: string) {
      try {
        return await httpAuth.get(`${LSP_APPLICATION_BASE}/${appId}/order/${orderId}`).then((response) => response.data)
      } catch (error) {
        console.log(error)
        return {
          status: false,
          message: ERR_MESSAGE(error),
        }
      }
    },
  },
}

export default LSP
